import { IElementImg } from "@/hooks/use-placeholders";
import { OnlyFeaturesState } from "@/store/features/features-slice";
import type { OnlyIElementsStore } from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import {
  IElementOrthomosaic,
  isIElementOrthomosaic,
  isIElementPhotogrammetryScan,
} from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import {
  IElement,
  IElementGaussianSplats,
  IElementGenericStream,
  isIElementGaussianSplats,
  isIElementGenericPointCloudStream,
  isIElementGenericStream,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import { selectAncestor } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

/** An element that can be rendered as the active element of a walk scene */
export type WalkSceneActiveElement =
  | IElementImg
  | IElementGenericStream
  | IElementOrthomosaic
  | IElementGaussianSplats;

/**
 * @param state The current app state
 * @param element to check
 * @returns true if it's a valid WalkSceneActiveElement
 */
export const selectIsWalkSceneActiveElement = curryAppSelector(
  createSelector(
    [
      (state: OnlyIElementsStore) => state.iElements,
      (state: OnlyIElementsStore & OnlyFeaturesState, element: IElement) =>
        element,
    ],
    (iElements, element): element is WalkSceneActiveElement =>
      isIElementImg360(element) ||
      isIElementImg2d(element) ||
      isIElementGenericStream(element) ||
      isIElementGaussianSplats(element) ||
      (isIElementOrthomosaic(element) &&
        !!selectAncestor(element, isIElementPhotogrammetryScan)({ iElements })),
  ),
);

export function sceneFilterFromElement(
  main: WalkSceneActiveElement,
): SceneFilter;
export function sceneFilterFromElement(
  main: WalkSceneActiveElement | undefined,
): SceneFilter | undefined;
/**
 * @param main element to check
 * @returns the scene filter for that element
 */
export function sceneFilterFromElement(
  main: WalkSceneActiveElement | undefined,
): SceneFilter | undefined {
  if (!main) {
    return;
  }
  if (
    isIElementImg360(main) ||
    isIElementImg2d(main) ||
    isIElementOrthomosaic(main)
  ) {
    return SceneFilter.Pano;
  }
  if (isIElementGenericPointCloudStream(main)) {
    return SceneFilter.PointCloud;
  }
  if (isIElementGaussianSplats(main)) {
    return SceneFilter.GaussianSplats;
  }
  return SceneFilter.Cad;
}
